import { GetSearchParamsDto } from '@app/data/dtos/search/get-search-params.dto';
import { SearchHistoryRequestDto } from '@app/data/dtos/search/search-history-request.dto';
import { SearchHistoryDto } from '@app/data/dtos/search/search-history.dto';
import { SearchModelCollectionDto } from '@app/data/dtos/search/search-model-collection.dto';
import { SearchModelItemsDto } from '@app/data/dtos/search/search-model-items.dto';
import { SearchModelDto } from '@app/data/dtos/search/search-model.dto';
import { SearchPublicationCollectionDto } from '@app/data/dtos/search/search-publication-collection.dto';
import { SearchPublicationItemsDto } from '@app/data/dtos/search/search-publication-items.dto';
import { SearchPublicationDto } from '@app/data/dtos/search/search-publication.dto';
import { SearchSectionCollectionDto } from '@app/data/dtos/search/search-section-collection.dto';
import { SearchSectionItemsDto } from '@app/data/dtos/search/search-section-items.dto';
import { SearchSectionDto } from '@app/data/dtos/search/search-section.dto';
import { SearchDto } from '@app/data/dtos/search/search.dto';
import { GetSearchParams } from '@app/entities/interfaces/search/get-search-params';
import { SearchHistoryRequest } from '@app/entities/models/search/search-history-request.model';
import { SearchHistory } from '@app/entities/models/search/search-history.model';
import { SearchModelCollection } from '@app/entities/models/search/search-model-collection.model';
import { SearchModelItems } from '@app/entities/models/search/search-model-items.model';
import { SearchModel } from '@app/entities/models/search/search-model.model';
import { SearchPublicationCollection } from '@app/entities/models/search/search-publication-collection.model';
import { SearchPublicationItems } from '@app/entities/models/search/search-publication-items.model';
import { SearchPublication } from '@app/entities/models/search/search-publication.model';
import { SearchSectionCollection } from '@app/entities/models/search/search-section-collection.model';
import { SearchSectionItems } from '@app/entities/models/search/search-section-items.model';
import { SearchSection } from '@app/entities/models/search/search-section.model';
import { Search } from '@app/entities/models/search/search.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

export const mapSearch: MapFromFn<SearchDto, Search> = (value: SearchDto): Search =>
  Builder.forModel(Search)
    .sections(mapSection(value.sections))
    .publications(mapPublication(value.publications))
    .modelSections(mapModel(value.model_sections))
    .build();

export const mapSection: MapFromFn<SearchSectionDto, SearchSection> = (value: SearchSectionDto): SearchSection =>
  Builder.forModel(SearchSection).showViewAll(value.show_view_all).items(value.items.map(mapSectionItem)).build();

export const mapSectionItem: MapFromFn<SearchSectionItemsDto, SearchSectionItems> = (
  value: SearchSectionItemsDto
): SearchSectionItems => Builder.forModel(SearchSectionItems).id(value.id).key(value.key).title(value.title).build();

export const mapPublication: MapFromFn<SearchPublicationDto, SearchPublication> = (
  value: SearchPublicationDto
): SearchPublication =>
  Builder.forModel(SearchPublication)
    .showViewAll(value.show_view_all)
    .items(value.items.map(mapPublicationItem))
    .build();

export const mapPublicationItem: MapFromFn<SearchPublicationItemsDto, SearchPublicationItems> = (
  value: SearchPublicationItemsDto
): SearchPublicationItems =>
  Builder.forModel(SearchPublicationItems)
    .id(value.id)
    .title(value.title)
    .date(value.date)
    .thumbnail(value.thumbnail)
    .translationKey(value.translation_key)
    .build();

export const mapModel: MapFromFn<SearchModelDto, SearchModel> = (value: SearchModelDto): SearchModel =>
  Builder.forModel(SearchModel).showViewAll(value.show_view_all).items(value.items.map(mapModelItem)).build();

export const mapModelItem: MapFromFn<SearchModelItemsDto, SearchModelItems> = (
  value: SearchModelItemsDto
): SearchModelItems => Builder.forModel(SearchModelItems).id(value.id).path(value.path).title(value.title).build();

export const mapSearchHistory: MapFromFn<SearchHistoryDto, SearchHistory> = (value: SearchHistoryDto): SearchHistory =>
  Builder.forModel(SearchHistory).history(value.history).build();

export const mapSearchHistoryRequest: MapFromFn<SearchHistoryRequest, SearchHistoryRequestDto> = (
  value: SearchHistoryRequest
): SearchHistoryRequestDto => Builder.forDto<SearchHistoryRequestDto>().search_query(value.searchQuery).build();

export const mapSearchParams = (value: GetSearchParams): GetSearchParamsDto => ({
  search_query: value.searchQuery,
  page: value.page ? Number(value.page) : undefined,
  page_size: value.pageSize ? Number(value.pageSize) : undefined
});

export const mapSearchSectionCollection: MapFromFn<SearchSectionCollectionDto, SearchSectionCollection> = (
  value: SearchSectionCollectionDto
): SearchSectionCollection =>
  Builder.forModel(SearchSectionCollection)
    .page(value.page)
    .pageSize(value.page_size)
    .totalItems(value.total_items)
    .totalPages(value.total_pages)
    .items(value.items.map(mapSectionItem))
    .build();

export const mapSearchPublicationCollection: MapFromFn<SearchPublicationCollectionDto, SearchPublicationCollection> = (
  value: SearchPublicationCollectionDto
): SearchPublicationCollection =>
  Builder.forModel(SearchPublicationCollection)
    .page(value.page)
    .pageSize(value.page_size)
    .totalItems(value.total_items)
    .totalPages(value.total_pages)
    .items(value.items.map(mapPublicationItem))
    .build();

export const mapSearchModelCollection: MapFromFn<SearchModelCollectionDto, SearchModelCollection> = (
  value: SearchModelCollectionDto
): SearchModelCollection =>
  Builder.forModel(SearchModelCollection)
    .page(value.page)
    .pageSize(value.page_size)
    .totalItems(value.total_items)
    .totalPages(value.total_pages)
    .items(value.items.map(mapModelItem))
    .build();
